import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import {
  AdminListCard,
  AdminListTable,
  ConfirmDeleteModal,
  useBatchSelection,
  type BatchAction,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useClientContacts, useContactMutations } from "../api";
import type { Contact } from "../types";

export const ContactsList = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const sel = useBatchSelection([]);
  const { rows, isLoading, isFetching, refetch } = useClientContacts(id);
  const { archive } = useContactMutations(id);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const selectedContacts = useMemo(
    () => rows.filter((r) => sel.selectedIds.includes(r.id)),
    [rows, sel.selectedIds]
  );

  const columns: ColumnDef<Contact>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => row.original.name,
    },
    {
      accessorKey: "title",
      header: "Title",
      cell: ({ row }) => row.original.title ?? "",
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => row.original.email ?? "",
    },
    {
      accessorKey: "phone",
      header: "Phone",
      cell: ({ row }) => row.original.phone ?? "",
    },
  ];

  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    const targets = [...selectedContacts];
    let failures = 0;
    for (const c of targets) {
      try {
        await archive(c.id);
      } catch {
        failures += 1;
      }
    }
    setToast(
      failures === 0
        ? { variant: "success", text: `${targets.length} contact(s) archived.` }
        : {
            variant: "danger",
            text: `Failed on ${failures} of ${targets.length} contact(s).`,
          }
    );
    sel.clear();
    refetch();
  };

  const batchActions: BatchAction[] = [
    {
      icon: faTrashCan,
      label: "Delete",
      onClick: () => setConfirmOpen(true),
      variant: "danger",
    },
  ];

  if (isLoading) return <ScopeStackSpinner />;

  return (
    <>
      <AdminListCard
        title="Contacts"
        onNew={() => navigate("new")}
        newButtonLabel="+ Add A Contact"
        batchActions={batchActions}
        selectedCount={sel.selectedIds.length}
      >
        <AdminListTable
          columns={columns}
          data={rows}
          isLoading={isFetching}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          onEdit={(contactId) => navigate(`${contactId}/edit`)}
          emptyMessage="No contacts found."
        />
      </AdminListCard>

      <ConfirmDeleteModal
        open={confirmOpen}
        title="Archive contacts?"
        message={`Are you sure you want to archive ${sel.selectedIds.length} contact(s)?`}
        confirmLabel="Archive"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default ContactsList;
